import { useQuery } from '@tanstack/react-query';
import type { ComparisonRun } from 'fuel-benchmark-contracts';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { orpc } from '../../apis/orpc-query';
import { Spinner } from '../../components/ui/Spinner';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { ComparisonPanel } from './ComparisonPanel';
import {
  MAX_COMPARE_VEHICLES,
  MIN_COMPARE_VEHICLES,
  parseVehicleIds,
  sameRunSelection,
  serializeVehicleIds,
  toggleVehicleId,
} from './compare-selection';
import { RunPicker } from './RunPicker';
import { ScenarioCards } from './ScenarioCards';
import { VehicleSelectTable } from './VehicleSelectTable';

const CATALOGUE_PAGE_SIZE = 200;

/** Long enough that unchecking three runs in a row is one request, short enough to feel immediate. */
const SELECTION_DEBOUNCE_MS = 400;

type RunSelection = Record<string, string[]>;

/**
 * Compare (plan §7), in three progressive steps: pick two to four vehicles, pick a scenario every one of
 * them has proven, then hand-pick the runs behind each figure. The URL carries the first two steps, so
 * `/compare?vehicleIds=a,b&scenarioId=x` is a shareable deep link and the vehicle detail page can hand a
 * comparison over half-started.
 */
export const ComparePage = () => {
  const { t } = useI18nTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [vehicleIds, setVehicleIds] = useState<string[]>(() => parseVehicleIds(searchParams.get('vehicleIds')));
  const [scenarioId, setScenarioId] = useState<string>(() => searchParams.get('scenarioId') ?? '');
  const [selection, setSelection] = useState<RunSelection | null>(null);
  const [debouncedSelection, setDebouncedSelection] = useState<RunSelection | null>(null);
  const [runUniverse, setRunUniverse] = useState<{ key: string; byVehicle: Record<string, ComparisonRun[]> } | null>(null);

  const hasEnoughVehicles = vehicleIds.length >= MIN_COMPARE_VEHICLES;
  const selectionKey = `${serializeVehicleIds(vehicleIds)}|${scenarioId}`;

  const vehicles = useQuery(
    orpc.vehicles.list.queryOptions({
      input: { page: 1, pageSize: CATALOGUE_PAGE_SIZE, sortBy: 'code', sortDirection: 'asc', status: 'ACTIVE' },
    }),
  );

  const common = useQuery(orpc.compare.commonScenarios.queryOptions({ input: { vehicleIds }, enabled: hasEnoughVehicles }));

  const comparison = useQuery(
    orpc.compare.compare.queryOptions({
      input: { vehicleIds, scenarioId, runSelection: debouncedSelection ?? 'ALL_VALID' },
      enabled: hasEnoughVehicles && scenarioId !== '',
      // A rejected selection (a run that is not comparable) is an answer, not a glitch worth retrying.
      retry: false,
    }),
  );

  // The URL is the source of truth for the first two steps, so a deep link and a click produce one state.
  useEffect(() => {
    const next = new URLSearchParams();

    if (vehicleIds.length > 0) {
      next.set('vehicleIds', serializeVehicleIds(vehicleIds));
    }

    if (scenarioId !== '') {
      next.set('scenarioId', scenarioId);
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [vehicleIds, scenarioId, searchParams, setSearchParams]);

  /**
   * The first answer for a vehicle/scenario pair is always the `ALL_VALID` one, so its run lists are the
   * complete set the picker offers — remembered here so a deselected run can be checked again.
   */
  useEffect(() => {
    const data = comparison.data;

    if (!data || runUniverse?.key === selectionKey) {
      return;
    }

    const byVehicle: Record<string, ComparisonRun[]> = {};
    const allRunIds: RunSelection = {};

    for (const entry of data.vehicles) {
      byVehicle[entry.vehicle.id] = entry.runs;
      allRunIds[entry.vehicle.id] = entry.runs.map((run) => run.id);
    }

    setRunUniverse({ key: selectionKey, byVehicle });
    setSelection(allRunIds);
    setDebouncedSelection(allRunIds);
  }, [comparison.data, runUniverse, selectionKey]);

  // Only the settled selection is sent, so unchecking several runs in a row is one comparison call.
  useEffect(() => {
    if (selection === null || sameRunSelection(selection, debouncedSelection)) {
      return;
    }

    const timer = window.setTimeout(() => setDebouncedSelection(selection), SELECTION_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [selection, debouncedSelection]);

  const commonScenarios = common.data?.scenarios ?? [];

  // A deep-linked scenario one of the vehicles has never tested cannot be compared; step B takes over.
  useEffect(() => {
    if (scenarioId === '' || !common.data) {
      return;
    }

    if (!common.data.scenarios.some((entry) => entry.scenario.id === scenarioId)) {
      setScenarioId('');
    }
  }, [common.data, scenarioId]);

  const resetSelection = (): void => {
    setSelection(null);
    setDebouncedSelection(null);
    setRunUniverse(null);
  };

  const onToggleVehicle = (vehicleId: string): void => {
    setVehicleIds((current) => toggleVehicleId(current, vehicleId));
    setScenarioId('');
    resetSelection();
  };

  const onSelectScenario = (nextScenarioId: string): void => {
    setScenarioId(nextScenarioId);
    resetSelection();
  };

  const onToggleRun = (vehicleId: string, runId: string): void => {
    setSelection((current) => {
      if (current === null) {
        return current;
      }

      const kept = current[vehicleId] ?? [];
      const next = kept.includes(runId) ? kept.filter((id) => id !== runId) : [...kept, runId];

      // A vehicle with no runs has nothing to compare, so the last one stays checked.
      return next.length === 0 ? current : { ...current, [vehicleId]: next };
    });
  };

  /** The label of a picker's heading: the comparison's own label first, the catalogue row as a fallback. */
  const labelOf = (vehicleId: string): string => {
    const compared = comparison.data?.vehicles.find((entry) => entry.vehicle.id === vehicleId);

    if (compared) {
      return compared.vehicle.label;
    }

    const listed = (vehicles.data?.items ?? []).find((vehicle) => vehicle.id === vehicleId);

    return listed ? `${listed.manufacturer} ${listed.model} ${listed.modelYear}` : vehicleId;
  };

  const codeOf = (vehicleId: string): string =>
    comparison.data?.vehicles.find((entry) => entry.vehicle.id === vehicleId)?.vehicle.code ??
    (vehicles.data?.items ?? []).find((vehicle) => vehicle.id === vehicleId)?.code ??
    '';

  const comparisonError = comparison.isError ? (serverMessage(comparison.error) ?? t('errors.loadFailed')) : null;
  const isStale = comparison.isFetching && comparison.data !== undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{t('compare.title')}</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t('compare.subtitle')}</p>
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-foreground">{t('compare.vehicles.title')}</h2>
          <p className="text-xs text-muted-foreground">{t('compare.vehicles.counter', { count: vehicleIds.length, max: MAX_COMPARE_VEHICLES })}</p>
        </div>

        <VehicleSelectTable
          vehicles={vehicles.data?.items ?? []}
          selectedIds={vehicleIds}
          onToggle={onToggleVehicle}
          isLoading={vehicles.isPending}
        />

        {!hasEnoughVehicles ? <p className="text-sm text-muted-foreground">{t('compare.vehicles.needTwo')}</p> : null}

        {common.data?.crossSegment ? (
          <p
            role="status"
            className="rounded-md border px-3 py-2 text-sm"
            style={{ color: '#a16207', borderColor: '#a1620733' }}
          >
            {t('compare.vehicles.crossSegment', {
              segments: [...new Set((common.data?.vehicles ?? []).map((vehicle) => vehicle.segmentName))].join(', '),
            })}
          </p>
        ) : null}
      </section>

      {hasEnoughVehicles ? (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-foreground">{t('compare.scenarios.title')}</h2>
          <p className="max-w-3xl text-xs text-muted-foreground">{t('compare.scenarios.hint')}</p>

          {common.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner label={t('app.loading')} />
              {t('app.loading')}
            </div>
          ) : common.isError ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {serverMessage(common.error) ?? t('errors.loadFailed')}
            </p>
          ) : (
            <ScenarioCards
              scenarios={commonScenarios}
              vehicles={common.data?.vehicles ?? []}
              selectedScenarioId={scenarioId}
              onSelect={onSelectScenario}
            />
          )}
        </section>
      ) : null}

      {hasEnoughVehicles && scenarioId !== '' ? (
        <section className="space-y-4">
          <h2 className="text-sm font-medium text-foreground">{t('compare.runs.title')}</h2>
          <p className="max-w-3xl text-xs text-muted-foreground">{t('compare.runs.hint')}</p>

          {runUniverse && selection ? (
            <div className="grid gap-3 lg:grid-cols-2">
              {vehicleIds.map((vehicleId) => (
                <RunPicker
                  key={vehicleId}
                  vehicleId={vehicleId}
                  vehicleLabel={labelOf(vehicleId)}
                  vehicleCode={codeOf(vehicleId)}
                  runs={runUniverse.byVehicle[vehicleId] ?? []}
                  selectedRunIds={selection[vehicleId] ?? []}
                  unit={comparison.data?.unit ?? ''}
                  onToggle={onToggleRun}
                />
              ))}
            </div>
          ) : null}

          {comparisonError ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {comparisonError}
            </p>
          ) : null}

          {comparison.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner label={t('app.loading')} />
              {t('app.loading')}
            </div>
          ) : null}

          {comparison.data ? (
            <ComparisonPanel
              comparison={comparison.data}
              isStale={isStale}
              runSelection={debouncedSelection ?? 'ALL_VALID'}
            />
          ) : null}
        </section>
      ) : null}
    </div>
  );
};
